import { useSearch } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { env } from "../config/env";
import { normalizeOperation } from "../lib/normalize-operation";
import { useRampsStore } from "../stores/use-ramps-store";
import {
	type AnalyticsEvent,
	type BaseAnalyticsProperties,
	type EntryEventProperties,
	type ExitEventProperties,
	type PageViewProperties,
	resolvePartner,
	type TransactionAmountProperties,
} from "../utils/analytics";

/**
 * Human-readable labels for each step in the Ramps flow.
 * Used in exit/abandonment events so PostHog dashboards show
 * meaningful names instead of raw URL paths.
 */
export const STEP_LABELS: Record<string, string> = {
	calculator: "Calculator",
	phone: "Phone Verification",
	name: "Name Entry",
	address: "Address Entry",
	"date-of-birth": "Date of Birth",
	identification: "ID Verification",
	"digital-offramp-info": "Digital Off-Ramp Info",
	"additional-info": "Additional Info",
	review: "Review & Confirm",
	status: "Transaction Status",
	"fraud-warning": "Fraud Warning",
	"kyc-verification": "KYC Verification",
	deposit: "Deposit Amount",
};

/**
 * Safely execute a PostHog SDK call. Catches any internal SDK errors
 * so that analytics failures never break the user flow.
 */
function safePosthog(fn: () => void): void {
	try {
		fn();
	} catch (_error) {
		// Analytics should never break the user flow
	}
}

/**
 * Custom hook to access PostHog analytics
 * Provides a clean interface for tracking events throughout the application
 * Automatically enriches events with environment, wallet_provider, session metadata
 */
export function useAnalytics() {
	const posthog = usePostHog();
	const search = useSearch({ strict: false });
	const store = useRampsStore();

	// Track page entry time for calculating time on page
	const pageEntryTimeRef = useRef<number>(Date.now());
	const previousPageRef = useRef<string | null>(null);

	// Partner attribution, resolved from the JWT on the URL at mount. Shared by
	// the super-property registration and every per-event property bag below:
	// per-event properties override super properties in PostHog, so a stale copy
	// in either place is enough to lose the attribution.
	const partnerInfo = useMemo(
		() =>
			resolvePartner({
				token:
					((search as Record<string, unknown>)?.token as string) ||
					((search as Record<string, unknown>)?.accesstoken as string) ||
					store.token,
				prepared: store.walletProvider,
				urlParam: (search as Record<string, unknown>)
					?.wallet_provider as string,
			}),
		[search, store.token, store.walletProvider],
	);

	// Get common properties that are enriched in all events
	const commonProperties = useMemo((): BaseAnalyticsProperties => {
		const searchParams = search as Record<string, unknown>;

		return {
			// Environment context
			environment: env.TARGET_ENV,
			api_version: store.apiVersion || "v3",

			// Session context
			session_id: posthog?.get_session_id?.() || undefined,
			timestamp: Date.now(),

			// User journey context
			page_path:
				typeof window !== "undefined" ? window.location.pathname : undefined,
			current_step:
				(searchParams?.step as string) || store.currentStep || undefined,
			wallet_provider: partnerInfo.wallet_provider,
			wallet_provider_known: partnerInfo.wallet_provider_known,

			// Device context
			user_agent:
				typeof navigator !== "undefined" ? navigator.userAgent : undefined,
			language: store.lang || (searchParams?.lang as string) || "en",

			// Referrer context
			referrer: typeof document !== "undefined" ? document.referrer : undefined,
		};
	}, [
		search,
		store.apiVersion,
		store.currentStep,
		store.lang,
		partnerInfo,
		posthog,
	]);

	// Register super properties on ALL events (including autocaptured, $pageview, $pageleave)
	useEffect(() => {
		if (posthog) {
			const searchParams = search as Record<string, unknown>;
			safePosthog(() =>
				posthog.register({
					environment: env.TARGET_ENV,
					api_version: store.apiVersion || "v3",
					wallet_provider: partnerInfo.wallet_provider,
					wallet_provider_known: partnerInfo.wallet_provider_known,
					operation: store.operation || undefined,
					// v3 sessions get their locale from /transactions/prepare, not the
					// URL, so the store value has to win once it lands.
					language: store.lang || (searchParams?.lang as string) || "en",
				}),
			);
		}
	}, [
		posthog,
		search,
		store.apiVersion,
		store.operation,
		store.lang,
		partnerInfo,
	]);

	/**
	 * Track a custom event with automatic property enrichment
	 */
	const trackEvent = useCallback(
		(
			eventName: AnalyticsEvent | string,
			properties?: Partial<BaseAnalyticsProperties> | Record<string, unknown>,
		) => {
			if (posthog) {
				const enrichedProperties = {
					...commonProperties,
					...properties,
				};

				safePosthog(() => posthog.capture(eventName, enrichedProperties));
			}
		},
		[posthog, commonProperties],
	);

	/**
	 * Track page view with enhanced metadata
	 */
	const trackPageView = useCallback(
		(
			pageName: string,
			additionalProps?: Partial<PageViewProperties> | Record<string, unknown>,
		) => {
			if (posthog) {
				const now = Date.now();
				const timeOnPreviousPage = now - pageEntryTimeRef.current;

				const pageViewProperties: PageViewProperties = {
					...commonProperties,
					page_name: pageName,
					page_title:
						typeof document !== "undefined" ? document.title : undefined,
					previous_page: previousPageRef.current || undefined,
					time_on_previous_page_ms: previousPageRef.current
						? timeOnPreviousPage
						: undefined,
					...additionalProps,
				};

				safePosthog(() =>
					posthog.capture("$pageview", {
						$current_url:
							typeof window !== "undefined" ? window.location.href : undefined,
						...pageViewProperties,
					}),
				);

				// Update refs for next page view
				previousPageRef.current = pageName;
				pageEntryTimeRef.current = now;

				// Track visited pages for pages_visited metric
				try {
					const visited = JSON.parse(
						sessionStorage.getItem("ramps_pages_visited") || "[]",
					);
					if (!visited.includes(pageName)) {
						visited.push(pageName);
						sessionStorage.setItem(
							"ramps_pages_visited",
							JSON.stringify(visited),
						);
					}
				} catch {
					/* sessionStorage may be unavailable */
				}
			}
		},
		[posthog, commonProperties],
	);

	/**
	 * Track session start with entry metadata
	 */
	const trackSessionStart = useCallback(
		(entryMetadata: Partial<EntryEventProperties>) => {
			if (posthog) {
				const searchParams = search as Record<string, unknown>;

				// Detect if running in WebView
				const userAgent =
					typeof navigator !== "undefined" ? navigator.userAgent : "";
				const isWebView =
					/WebView|wv/.test(userAgent) ||
					(typeof window !== "undefined" &&
						(window as typeof window & { ReactNativeWebView?: unknown })
							.ReactNativeWebView !== undefined);

				const webviewPlatform = isWebView
					? /iPhone|iPad|iPod/.test(userAgent)
						? "ios"
						: /Android/.test(userAgent)
							? "android"
							: "unknown"
					: undefined;

				const entryProperties: EntryEventProperties = {
					...commonProperties,
					wallet_provider: partnerInfo.wallet_provider,
					has_token: !!(searchParams?.token || searchParams?.accesstoken),
					has_transaction_id: !!searchParams?.transaction_id,
					operation:
						normalizeOperation(searchParams?.operation as string) || "withdraw",
					asset_code: (searchParams?.asset_code as string) || "USDC",
					is_webview: isWebView,
					webview_platform: webviewPlatform,
					entry_source: isWebView
						? "webview"
						: document.referrer
							? "referral"
							: "direct",
					initial_landing_page:
						typeof window !== "undefined"
							? window.location.pathname
							: undefined,
					...entryMetadata,
				};

				safePosthog(() => {
					posthog.capture("session_started", entryProperties);

					// Set partner group for native partner-level funnels and breakdowns.
					// Any resolved partner counts — gating this on an allowlist is what
					// kept partner groups empty.
					if (partnerInfo.wallet_provider !== "unknown") {
						posthog.group("partner", partnerInfo.wallet_provider, {
							name: partnerInfo.wallet_provider,
						});
					}
				});
			}
		},
		[posthog, commonProperties, search, partnerInfo],
	);

	/**
	 * Track user exit from the application
	 */
	const trackExit = useCallback(
		(
			exitType: ExitEventProperties["exit_type"],
			exitMetadata?: Partial<ExitEventProperties>,
		) => {
			if (posthog) {
				const sessionStartTime = pageEntryTimeRef.current;
				const now = Date.now();
				const timeInSession = now - sessionStartTime;

				const searchParams = search as Record<string, unknown>;
				const stepKey =
					(searchParams?.step as string) || store.currentStep || "unknown";

				const isDeposit = store.operation === "deposit";
				const exitProperties: ExitEventProperties = {
					...commonProperties,
					exit_type: exitType,
					last_page: STEP_LABELS[stepKey] || stepKey,
					time_in_session_ms: timeInSession,
					transaction_id: store.transactionId || undefined,
					transaction_status: store.currentStep || undefined,
					amount: store.amount ? Number.parseFloat(store.amount) : undefined,
					source_currency: isDeposit
						? store.payoutCurrency || undefined
						: store.assetCode || undefined,
					destination_currency: isDeposit
						? store.assetCode || undefined
						: store.payoutCurrency || undefined,
					service_option: store.serviceOption || undefined,
					...exitMetadata,
				};

				const eventMap = {
					completed: "transaction_completed",
					cancelled: "transaction_cancelled",
					abandoned: "session_abandoned",
					error: "error_occurred",
				};

				safePosthog(() => posthog.capture(eventMap[exitType], exitProperties));
			}
		},
		[
			posthog,
			commonProperties,
			search,
			store.transactionId,
			store.currentStep,
			store.amount,
			store.assetCode,
			store.payoutCurrency,
			store.operation,
			store.serviceOption,
		],
	);

	/**
	 * Alias a previous distinct_id to a new one, merging person records in PostHog.
	 * Call this when a user transitions from a temporary identity (tx_, phone_)
	 * to a stable one (profile_).
	 */
	const aliasUser = useCallback(
		(previousId: string, canonicalId: string) => {
			if (posthog) {
				safePosthog(() => posthog.alias(canonicalId, previousId));
			}
		},
		[posthog],
	);

	/**
	 * Capture the finalized transaction amount early in the funnel (at calculator continue).
	 * Provides a standalone numeric amount event with full currency context.
	 */
	const trackTransactionAmount = useCallback(
		(props: {
			amount: number;
			sourceCurrency: string;
			destinationCurrency: string;
			serviceOption?: string | null;
			exchangeRate?: number;
			estimatedFee?: number;
		}) => {
			if (posthog && props.amount > 0) {
				const amountProperties: TransactionAmountProperties = {
					...commonProperties,
					transaction_amount: props.amount,
					source_currency: props.sourceCurrency,
					destination_currency: props.destinationCurrency,
					service_option: props.serviceOption,
					exchange_rate: props.exchangeRate,
					estimated_fee: props.estimatedFee,
					transaction_id: store.transactionId || undefined,
				};
				safePosthog(() =>
					posthog.capture("transaction_amount_captured", amountProperties),
				);
			}
		},
		[posthog, commonProperties, store.transactionId],
	);

	/**
	 * Identify user with properties
	 */
	const identifyUser = useCallback(
		(userId: string, properties?: Record<string, unknown>) => {
			if (posthog) {
				const searchParams = search as Record<string, unknown>;

				const userProperties = {
					environment: env.TARGET_ENV,
					wallet_provider: partnerInfo.wallet_provider,
					language: store.lang || (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
					...properties,
				};

				safePosthog(() => posthog.identify(userId, userProperties));
			}
		},
		[posthog, search, store.apiVersion, store.lang, partnerInfo],
	);

	/**
	 * Reset user session
	 */
	const resetUser = useCallback(() => {
		if (posthog) {
			safePosthog(() => posthog.reset());
		}
	}, [posthog]);

	/**
	 * Set user properties
	 */
	const setUserProperties = useCallback(
		(properties: Record<string, unknown>) => {
			if (posthog) {
				// posthog.people is deprecated as of posthog-js 1.4xx.
				safePosthog(() => posthog.setPersonProperties(properties));
			}
		},
		[posthog],
	);

	return {
		trackEvent,
		trackPageView,
		trackSessionStart,
		trackExit,
		identifyUser,
		aliasUser,
		trackTransactionAmount,
		resetUser,
		setUserProperties,
		partnerInfo, // Resolved partner attribution, shared with consumers
		posthog, // Direct access to PostHog instance if needed
	};
}
